import React, { ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';
import makeStyles from '@mui/styles/makeStyles';
import { grey, red } from '@mui/material/colors';
import { Box, Button, Stack, Typography } from '@mui/material';
import { SubsidyCapType } from '@trnsact/trnsact-shared-types';
import { booleanOptions } from 'modules/financeProgram/constants';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputField, FormRadioGroup, InputField, InterestInputField, SelectField } from 'components/form';
import { FinanceStructureSchema } from '../model';
import { markupOptions } from '../../../constants';
import { Structures, MarkupMethods } from '../types';

export const StructureConfiguration = ({ netInvoiceAmount }: { netInvoiceAmount: number }) => {
  const classes = useStyles();

  const { control, setValue } = useFormContext<z.infer<typeof FinanceStructureSchema>>();
  const financeQuote = useWatch({ control, name: 'financeQuote' });

  const [renderType, setRenderType] = useState<'buttons' | 'fields'>('buttons');

  useEffect(() => {
    setRenderType(financeQuote.structure !== Structures.None ? 'fields' : 'buttons');
  }, [financeQuote.structure, financeQuote.createProposalWithExistingProgram]);

  // If I don't have this, the form does not initialize the subsidyCapConfiguration field
  useEffect(() => {
    if (!financeQuote.subsidyCapConfiguration) {
      setValue('financeQuote.subsidyCapConfiguration', {
        lenderName: '',
        maxSubsidyAmount: 0,
        lenderMarketRates: [],
        netInvoicePercentage: 0,
        netInvoiceAmount: netInvoiceAmount ?? 0,
        subsidyCapType: SubsidyCapType.FixedAmount,
      });
    }
  }, []);

  const handleChangeMarkup = () => {
    setRenderType('fields');
    setValue('financeQuote.structure', Structures.Markup);
    setValue('financeQuote.markupMethod', MarkupMethods.MarkupPercentage);
  };

  const handleChangeSubsidy = () => {
    setRenderType('fields');
    setValue('financeQuote.structure', Structures.Subsidy);
  };

  const handleChangeRemove = () => {
    setValue('financeQuote.markupRate', null);
    setValue('financeQuote.subsidyType', undefined);
    setValue('financeQuote.markupMethod', undefined);
    setValue('financeQuote.configureSubsidyCap', false);
    setValue('financeQuote.commonLenderMarketRate', 0);
    setValue('financeQuote.subsidyCapConfiguration', undefined);
    setValue('financeQuote.structure', Structures.None);

    setValue('financeQuote.dealerMarkup', null);

    setRenderType('buttons');
  };

  const markupFields: Record<MarkupMethods, ReactNode> = {
    [MarkupMethods.MarkupPercentage]: (
      <InterestInputField
        control={control}
        label="Dealer Markup (%)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true }}
      />
    ),
    [MarkupMethods.MarkupDollar]: (
      <CurrencyInputField
        control={control}
        label="Dealer Markup ($)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true }}
      />
    ),
    [MarkupMethods.CustomerRate]: (
      <InterestInputField
        control={control}
        label="Customer Rate (%)"
        name="financeQuote.markupRate"
        textFieldProps={{ fullWidth: true }}
      />
    ),
  };

  const fieldsByConfigurationType = {
    [Structures.None]: null,
    [Structures.Markup]: {
      label: 'Set Markup',
      radioGroupField: (
        <Controller
          control={control}
          name="financeQuote.markupMethod"
          render={({ field }) => <FormRadioGroup row options={markupOptions} {...field} />}
        />
      ),
      valueField: financeQuote.markupMethod ? markupFields[financeQuote.markupMethod] : null,
    },
    [Structures.Subsidy]: {
      label: 'Subsidy Configuration',
      radioGroupField: null,
      valueField: (
        <Stack className="subsidyCapConfiguration">
          <Stack direction="row" gap="1rem">
            <SelectField
              control={control}
              name="financeQuote.configureSubsidyCap"
              label="Configure Subsidy Cap"
              options={booleanOptions}
              defaultValue={false}
            />

            {financeQuote.configureSubsidyCap && (
              <InputField
                control={control}
                label="Lender Name"
                name="financeQuote.subsidyCapConfiguration.lenderName"
                inputProps={{ required: true }}
              />
            )}
          </Stack>

          {financeQuote.configureSubsidyCap && (
            <Stack direction="row" gap="1rem">
              <SelectField
                control={control}
                label="Cap Type"
                defaultValue={SubsidyCapType.FixedAmount}
                name="financeQuote.subsidyCapConfiguration.subsidyCapType"
                options={[
                  { label: 'Fixed $ Amount', value: SubsidyCapType.FixedAmount },
                  { label: 'Dealer Net Invoice %', value: SubsidyCapType.NetInvoicePercentage },
                ]}
              />

              {financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount ? (
                <CurrencyInputField
                  control={control}
                  label="Subsidy Cap Amount"
                  name="financeQuote.subsidyCapConfiguration.maxSubsidyAmount"
                  textFieldProps={{
                    required: true,
                  }}
                />
              ) : financeQuote.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage ? (
                <>
                  <CurrencyInputField
                    control={control}
                    label="Dealer Net Invoice Amount ($)"
                    name="financeQuote.subsidyCapConfiguration.netInvoiceAmount"
                    textFieldProps={{
                      required: true,
                    }}
                  />

                  <InterestInputField
                    control={control}
                    label="Subsidy Cap Percentage"
                    name="financeQuote.subsidyCapConfiguration.netInvoicePercentage"
                    textFieldProps={{
                      required: true,
                    }}
                  />
                </>
              ) : null}
            </Stack>
          )}

          {financeQuote.configureSubsidyCap && !financeQuote.specifyRate && (
            <InterestInputField
              required
              control={control}
              label="Lender Market Rate"
              name="financeQuote.commonLenderMarketRate"
              textFieldProps={{ fullWidth: true }}
            />
          )}
        </Stack>
      ),
    },
  };

  const renderByType: Record<'buttons' | 'fields', ReactNode> = {
    buttons: (
      <Box className={classes.btnsContainer}>
        <Button variant="contained" size="small" onClick={handleChangeMarkup}>
          Configure Markup
        </Button>
        <Button variant="contained" size="small" onClick={handleChangeSubsidy}>
          Configure Subsidy
        </Button>
      </Box>
    ),
    fields: (
      <Box className={classes.fieldsContainer}>
        <Box className="radioSection">
          <Box className="radioSectionField">
            <Typography component="span" variant="subtitle2">
              {fieldsByConfigurationType[financeQuote.structure]?.label}
            </Typography>

            {fieldsByConfigurationType[financeQuote.structure]?.radioGroupField}
          </Box>

          <Button variant="text" size="small" style={{ color: red['500'] }} onClick={handleChangeRemove}>
            Remove
          </Button>
        </Box>

        {fieldsByConfigurationType[financeQuote.structure]?.valueField}
      </Box>
    ),
  };

  return <>{renderByType[renderType]}</>;
};

const useStyles = makeStyles({
  btnsContainer: {
    gap: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
  fieldsContainer: {
    padding: '0.75rem',
    borderRadius: '2px',
    backgroundColor: grey['100'],

    '& .radioSection': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',

      '& .radioSectionField': {
        gap: '1rem',
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
  formRow: {
    gap: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    paddingRight: '4px',

    '& .subsidyCapConfiguration': {
      gap: '16px',
      padding: '8px',
    },
  },
});
